import { CommonModule } from '@angular/common';
import { Component } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { Router, ActivatedRoute, RouterModule } from '@angular/router';
import { PrimaryBtnComponent } from 'app/components/ui-kit/buttons/primary-btn/primary-btn.component';
import { SvgLoaderComponent } from 'app/components/ui-kit/images/svg-loader/svg-loader.component';
import { BaseInputComponent } from 'app/components/ui-kit/inputs/base-input/base-input.component';
import { PasswordStrengthInputComponent } from 'app/components/ui-kit/inputs/password-strength-input.component';
import { SplitPageWrapperComponent } from 'app/components/ui-kit/page-components/split-page-wrapper/split-page-wrapper.component';
import { AuthService } from 'app/services/auth.service';
import { ThemeService } from 'app/services/theme.service';
import { BLOCKED_PASSWORDS } from 'app/constants/auth-constants';
import { AlertMessageComponent } from 'app/components/ui-kit/alerts/alert-message/alert-message.component';
import { SpinnerComponent } from 'app/components/ui-kit/loaders/spinner/spinner.component';

@Component({
  selector: 'app-register',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    SplitPageWrapperComponent,
    PrimaryBtnComponent,
    SvgLoaderComponent,
    BaseInputComponent,
    PasswordStrengthInputComponent,
    RouterModule,
    AlertMessageComponent,
    SpinnerComponent,
  ],
  templateUrl: './register.component.html',
  styleUrl: './register.component.scss',
})
export class RegisterComponent {
  email = '';
  password = '';
  firstName = '';
  lastName = '';
  errorMessage = '';

  currentTheme: string = '';
  ctaText: string = ' ';

  isRegistering: boolean = false;
  isAuthenticatingGoogle: boolean = false;

  constructor(
    private authService: AuthService,
    private router: Router,
    private route: ActivatedRoute,
    private themeService: ThemeService,
  ) {
    const theme = this.themeService.getCurrentTheme();
    this.currentTheme = theme;
  }

  ngAfterViewInit() {
    this.typeOutText('Start finding trusted flight sharing options');
  }

  async signUp() {
    if (BLOCKED_PASSWORDS.includes(this.password.toLowerCase())) {
      this.errorMessage = 'This password is too common and easily guessed';
      return;
    }
    if (this.password.length < 8) {
      this.errorMessage = 'This password is too weak.';
      return;
    }
    this.isRegistering = true;
    try {
      await this.authService.createAccount(
        this.email,
        this.password,
        this.firstName,
        this.lastName,
      );
      this.errorMessage = '';
      this.redirectAfterCreate();
    } catch (error) {
      this.errorMessage = this.formatAuthError(error);
      this.isRegistering = false;
    }
  }

  async signInWithGoogle() {
    this.isRegistering = true;
    try {
      await this.authService.registerWithGoogle();
      this.redirectAfterCreate();
    } catch (error: any) {
      if (error?.message === 'wrong-password-for-link') {
        this.errorMessage =
          'Incorrect password. Please sign in with your email and password to link accounts.';
      } else if (
        error?.message === 'account-exists-with-different-signin-method'
      ) {
        this.errorMessage =
          'This email is already registered. Try signing in instead.';
      } else if (error?.message === 'link-cancelled') {
        this.errorMessage = 'Linking cancelled.';
      } else {
        this.errorMessage = this.formatAuthError(error);
      }
      this.isRegistering = false;
    }
  }

  private redirectAfterCreate() {
    this.router.navigateByUrl('onboard/verify-phone');
    this.errorMessage = '';
  }

  typeOutText(fullText: string, speed: number = 20): void {
    this.ctaText = ' '; // reset before typing
    let index = 0;

    const interval = setInterval(() => {
      this.ctaText += fullText.charAt(index);
      index++;

      if (index >= fullText.length) {
        clearInterval(interval);
      }
    }, speed);
  }

  resetError() {
    this.errorMessage = null;
  }

  formatAuthError(error: any): string {
    const raw = typeof error === 'string' ? error : error?.message || '';

    const cleaned = raw
      .replace(/^Firebase:\s*/i, '')
      .replace(/^Error\s*/i, '')
      .replace(/[\(\)]/g, '')
      .trim();

    const friendlyMap: Record<string, string> = {
      'popup-closed-by-user': 'Google popup was closed before completing.',
      'user-not-found': 'No user found with that email.',
      'wrong-password': 'Incorrect password.',
      'email-already-in-use': 'Unable to create account with this email.',
      'popup-blocked': 'Google Popup was blocked by the browser.',
      'network-request-failed': 'Network error. Please check your connection.',
      'too-many-requests': 'Too many attempts. Try again later.',
      'auth/invalid-email': 'Invalid Email or Format'
    };

    const lower = cleaned.toLowerCase();
    for (const code in friendlyMap) {
      if (lower.includes(code)) {
        return friendlyMap[code];
      }
    }

    return 'An unexpected error occurred. Please try again.';
  }

}
